/**
 * UnifiedAdminControl.js
 * 
 * Complete unified admin management component.
 * Handles both admin status AND feature access permissions in one interface.
 * 
 * Features:
 * - Select user from list with real-time status
 * - Manage admin status (promote/demote)
 * - Manage feature access (admin_panel, audiometry, inventory, etc.)
 * - Atomic operations (secure with password protection)
 * - Enhanced UI with user activity, permissions preview
 * - Real-time feedback and error handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { backendOrigin } from '../utils/backendOrigin';
import MuiAlert from '../utils/muiAlerts';
import { sanitizeText } from '../utils/sanitize';
import { ListLoadingSkeleton } from './LoadingSkeleton';
import EmptyState from './EmptyState';
import {
    Box, Paper, Typography, Grid, TextField, Button, CircularProgress,
    Avatar, Divider, Switch, useTheme, Alert, Chip, FormGroup, FormControlLabel,
    LinearProgress, Tooltip, Card, CardContent
} from '@mui/material';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import AccessibleRoundedIcon from '@mui/icons-material/AccessibleRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';

// Feature access keys that users can be granted
const FEATURE_ACCESS_KEYS = [
    'admin_panel',
    'audiometry',
    'generate_invoice',
    'inventory',
    'sales_report',
    'patients',
    'service',
];

const defaultAccess = FEATURE_ACCESS_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
}, {});

const normalizeAccess = (incomingAccess = {}) => {
    return FEATURE_ACCESS_KEYS.reduce((acc, key) => {
        acc[key] = Boolean(incomingAccess?.[key]);
        return acc;
    }, {});
};

const UnifiedAdminControl = ({ currentUserInfo, adminList = [] }) => {
    const theme = useTheme();

    // ✅ State Management
    const [userList, setUserList] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isAdminToggleState, setIsAdminToggleState] = useState(false);
    const [selectedUserAccess, setSelectedUserAccess] = useState(defaultAccess);
    const [adminPassword, setAdminPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [feedbackType, setFeedbackType] = useState(''); // 'success', 'error', 'warning'

    // ✅ Fetch users function - memoized with useCallback to prevent unnecessary re-renders
    const fetchUserList = useCallback(async () => {
        try {
            setIsLoadingUsers(true);
            const token = await currentUserInfo.getIdToken();

            const response = await axios.post(
                `${backendOrigin}/get-user-list`,
                {
                    current_user_uid: currentUserInfo.uid,
                    current_user_name: currentUserInfo.displayName,
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data?.operation === 'success' && response.data?.info) {
                setUserList(response.data.info);
            }
        } catch (error) {
            console.error('Failed to fetch user list:', error);
            setFeedbackMessage('Failed to load user list');
            setFeedbackType('error');
        } finally {
            setIsLoadingUsers(false);
        }
    }, [currentUserInfo]);

    // ✅ Fetch users on mount
    useEffect(() => {
        fetchUserList();
    }, [fetchUserList]);

    // ✅ Update both admin status AND feature access when user is selected
    useEffect(() => {
        if (selectedUser) {
            const isCurrentlyAdmin = adminList.includes(selectedUser.id);
            setIsAdminToggleState(isCurrentlyAdmin);
            setSelectedUserAccess(normalizeAccess(selectedUser?.auth_access || {}));
            setFeedbackMessage(null);
            setAdminPassword(""); // Clear password when switching users
        }
    }, [selectedUser, adminList]);

    const handleAdminToggle = (newState) => {
        setIsAdminToggleState(newState);
        setFeedbackMessage(null);
    };

    const handleAccessToggle = (featureKey) => {
        setSelectedUserAccess({ ...selectedUserAccess, [featureKey]: !selectedUserAccess[featureKey] });
        setFeedbackMessage(null);
    };

    const handleSubmit = async () => {
        // Validation
        if (!selectedUser) {
            MuiAlert.fire('Oops!', 'Please select a user first', 'warning');
            return;
        }

        if (!adminPassword) {
            MuiAlert.fire('Oops!', 'Enter admin password to proceed', 'warning');
            return;
        }

        // Show confirmation
        const changes = [];
        const wasAdmin = adminList.includes(selectedUser.id);
        if (isAdminToggleState !== wasAdmin) {
            changes.push(isAdminToggleState ? 'promote to admin' : 'demote from admin');
        }
        if (Object.values(selectedUserAccess).some(v => v)) {
            changes.push('update feature access');
        }

        if (changes.length === 0) {
            MuiAlert.fire('Info', 'No changes detected', 'info');
            return;
        }

        const confirmationText = `Update ${selectedUser.user_name}?\n\nChanges: ${changes.join(', ')}`;

        try {
            const confirmed = await MuiAlert.fire(
                'Confirm',
                confirmationText,
                'question',
                true
            );

            if (!confirmed.isConfirmed) return;

            // Submit to backend
            setIsSubmitting(true);
            const token = await currentUserInfo.getIdToken();

            // Update both admin status and user access in one call
            const response = await axios.post(
                `${backendOrigin}/set-admin-status`,
                {
                    user_id: selectedUser.id,
                    is_admin: isAdminToggleState,
                    user_access: selectedUserAccess,
                    admin_password: adminPassword,
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setIsSubmitting(false);
            setAdminPassword(""); // Clear password on submit

            if (response.data?.operation === 'success') {
                setFeedbackMessage(sanitizeText(response.data.message));
                setFeedbackType('success');

                // Show success alert
                MuiAlert.fire(
                    'Success!',
                    sanitizeText(response.data.message),
                    'success'
                ).then(() => {
                    window.location.reload();
                });
            } else {
                setFeedbackMessage(sanitizeText(response.data?.message || 'Operation failed'));
                setFeedbackType('error');
                MuiAlert.fire('Error!', sanitizeText(response.data?.message || 'Failed to update user'), 'error');
            }
        } catch (error) {
            setIsSubmitting(false);
            setAdminPassword(""); // Clear password on error for security

            let errorMessage = error.response?.data?.message || error.message || 'An error occurred';
            errorMessage = sanitizeText(errorMessage);

            // Handle specific error cases
            if (error.response?.status === 403) {
                errorMessage = 'Invalid admin password';
            } else if (error.response?.status === 404) {
                errorMessage = 'User not found';
            } else if (error.response?.status === 400) {
                errorMessage = sanitizeText(error.response.data?.message || 'Request validation failed');
            }

            setFeedbackMessage(errorMessage);
            setFeedbackType('error');
            MuiAlert.fire('Error!', errorMessage, 'error');
            console.error('User update error:', error);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <AdminPanelSettingsRoundedIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, m: 0 }}>
                        Complete Admin Management
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Manage admin status and feature permissions centrally
                    </Typography>
                </Box>
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2.5}>
                {/* User List Panel */}
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{ 
                            p: 1.5,
                            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                            borderBottom: `1px solid ${theme.palette.divider}`
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                👥 User List
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {userList.length} {userList.length === 1 ? 'user' : 'users'} found
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                            {isLoadingUsers ? (
                                <ListLoadingSkeleton count={6} height={52} />
                            ) : userList.length === 0 ? (
                                <EmptyState title="No users" description="Users appear after first sign-in" />
                            ) : (
                                userList.map((user) => {
                                    const isUserAdmin = adminList.includes(user.id);
                                    const isSelected = selectedUser?.id === user.id;
                                    const hasFeatureAccess = user.auth_access && Object.values(user.auth_access).some(v => v);

                                    return (
                                        <Tooltip key={user.id} title={`${isUserAdmin ? 'Admin' : 'User'} • ${hasFeatureAccess ? 'Has access' : 'No access'}`} placement="right">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    p: 1.25,
                                                    mb: 0.75,
                                                    borderRadius: 1.5,
                                                    border: isSelected ? '2px solid' : '1px solid transparent',
                                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected
                                                        ? theme.palette.mode === 'dark'
                                                            ? 'rgba(25,118,210,0.2)'
                                                            : 'rgba(25,118,210,0.08)'
                                                        : 'transparent',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        backgroundColor: theme.palette.action.hover,
                                                    },
                                                }}
                                                onClick={() => setSelectedUser(user)}
                                            >
                                                <Avatar 
                                                    src={user.user_photo} 
                                                    alt={user.user_name} 
                                                    referrerPolicy="no-referrer"
                                                    sx={{ width: 36, height: 36 }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography 
                                                        sx={{ 
                                                            fontWeight: isSelected ? 600 : 500,
                                                            fontSize: '0.95rem',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {user.user_name}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                                                        {isUserAdmin && (
                                                            <Chip
                                                                icon={<VerifiedUserRoundedIcon />}
                                                                label="Admin"
                                                                size="small"
                                                                variant="filled"
                                                                sx={{ height: 18, fontSize: '0.7rem', fontWeight: 600 }}
                                                                color="warning"
                                                            />
                                                        )}
                                                        {hasFeatureAccess && (
                                                            <Chip
                                                                icon={<CheckCircleRoundedIcon />}
                                                                label="Active"
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ height: 18, fontSize: '0.7rem' }}
                                                                color="success"
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Tooltip>
                                    );
                                })
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Control & Configuration Panel */}
                <Grid item xs={12} md={8}>
                    {selectedUser ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* User Profile Card */}
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs="auto">
                                        <Avatar
                                            src={selectedUser.user_photo}
                                            alt={selectedUser.user_name}
                                            referrerPolicy="no-referrer"
                                            sx={{ width: 64, height: 64 }}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {selectedUser.user_name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedUser.user_email}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            {adminList.includes(selectedUser.id) ? (
                                                <Chip
                                                    icon={<VerifiedUserRoundedIcon />}
                                                    label="Authorized Administrator"
                                                    size="small"
                                                    color="warning"
                                                    variant="filled"
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<AccessibleRoundedIcon />}
                                                    label="Regular User"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Admin Status & Access Summary */}
                            <Grid container spacing={1.5}>
                                {/* Admin Status Card */}
                                <Grid item xs={12} sm={6}>
                                    <Card sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                {adminList.includes(selectedUser.id) ? (
                                                    <SecurityRoundedIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                                                ) : (
                                                    <BlockRoundedIcon sx={{ color: 'action.disabled', fontSize: 28 }} />
                                                )}
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        Admin Status
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {adminList.includes(selectedUser.id) ? 'Has admin privileges' : 'Standard user role'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {isAdminToggleState ? 'Admin' : 'User'}
                                                </Typography>
                                                <Switch
                                                    checked={isAdminToggleState}
                                                    onChange={(e) => handleAdminToggle(e.target.checked)}
                                                    disabled={isSubmitting}
                                                    size="medium"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Feature Access Summary */}
                                <Grid item xs={12} sm={6}>
                                    <Card sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                <AccessibleRoundedIcon sx={{ color: 'info.main', fontSize: 28 }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        Feature Access
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {Object.values(selectedUserAccess).filter(Boolean).length} of {FEATURE_ACCESS_KEYS.length} enabled
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Divider sx={{ my: 1 }} />
                                            <LinearProgress 
                                                variant="determinate"
                                                value={(Object.values(selectedUserAccess).filter(Boolean).length / FEATURE_ACCESS_KEYS.length) * 100}
                                                sx={{ height: 6, borderRadius: 3 }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Feature Access Controls */}
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessibleRoundedIcon sx={{ fontSize: 20 }} />
                                    Feature Permissions
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                    Select which features this user can access
                                </Typography>
                                <FormGroup>
                                    <Grid container spacing={1}>
                                        {FEATURE_ACCESS_KEYS.map((featureKey) => (
                                            <Grid item xs={12} sm={6} key={featureKey}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={selectedUserAccess[featureKey]}
                                                            onChange={() => handleAccessToggle(featureKey)}
                                                            disabled={isSubmitting}
                                                            size="medium"
                                                        />
                                                    }
                                                    label={
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                textTransform: 'capitalize',
                                                                fontWeight: selectedUserAccess[featureKey] ? 600 : 500,
                                                                ml: 0.5
                                                            }}
                                                        >
                                                            {featureKey.replace(/_/g, ' ')}
                                                            {selectedUserAccess[featureKey] && (
                                                                <CheckCircleRoundedIcon sx={{ fontSize: 16, ml: 0.5, color: 'success.main', marginTop: '-2px' }} />
                                                            )}
                                                        </Typography>
                                                    }
                                                    sx={{ m: 0, mb: 0.5 }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </FormGroup>
                            </Paper>

                            {/* Password & Submit */}
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SecurityRoundedIcon sx={{ fontSize: 20 }} />
                                    Confirm Changes
                                </Typography>
                                <TextField
                                    label="Admin Password"
                                    type="password"
                                    size="small"
                                    fullWidth
                                    value={adminPassword}
                                    onChange={(e) => setAdminPassword(e.target.value)}
                                    placeholder="Enter ADMIN_ROLE_UPDATE_PASSWORD"
                                    disabled={isSubmitting}
                                    error={feedbackType === 'error' && !adminPassword}
                                    helperText="🔐 Required for security"
                                    sx={{ mb: 2 }}
                                />

                                {feedbackMessage && (
                                    <Alert
                                        severity={feedbackType}
                                        sx={{ mb: 2 }}
                                        onClose={() => setFeedbackMessage(null)}
                                    >
                                        {feedbackMessage}
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        disabled={isSubmitting || !selectedUser}
                                        onClick={handleSubmit}
                                        size="large"
                                        startIcon={isSubmitting ? <CircularProgress size={20} /> : isAdminToggleState ? <VerifiedUserRoundedIcon /> : <ErrorRoundedIcon />}
                                        sx={{
                                            fontSize: '0.95rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            backgroundColor: isAdminToggleState ? 'warning.main' : 'primary.main'
                                        }}
                                    >
                                        {isSubmitting ? 'Processing...' : (
                                            adminList.includes(selectedUser.id) 
                                                ? isAdminToggleState ? 'Keep as Admin' : 'Remove Admin Access'
                                                : isAdminToggleState ? 'Promote to Admin' : 'Update Access Only'
                                        )}
                                    </Button>
                                </Box>

                                <Alert severity="info" sx={{ mt: 2, mb: 0 }}>
                                    <Typography variant="caption">
                                        <strong>What updates:</strong> {isAdminToggleState ? 'User will gain admin privileges with selected features' : 'User access will be updated with selected features only'}
                                    </Typography>
                                </Alert>
                            </Paper>
                        </Box>
                    ) : (
                        <Paper variant="outlined" sx={{ p: 4 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <AdminPanelSettingsRoundedIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 1.5 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    No User Selected
                                </Typography>
                                <Typography color="text.secondary">
                                    Select a user from the list on the left to manage their admin status and feature access
                                </Typography>
                            </Box>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default UnifiedAdminControl;
